import React from 'react';
import { motion } from 'framer-motion';
import { Truck, MapPin } from 'lucide-react';

const CheckoutForm = ({ customerInfo, handleInputChange, formErrors, shippingMethod, setShippingMethod, pickupLocation, setPickupLocation, sameAsBilling, setSameAsBilling, inputClass }) => (
  <div className="lg:col-span-3 space-y-8">
    {/* Logo */}
    <div className="flex justify-center mb-2">
      <img
        src="https://customer-assets.emergentagent.com/job_bee-harvest-6/artifacts/5m5uevnv_Ricky%27s%20Bees%20Logo.png"
        alt="Ricky's Bees"
        className="h-24 w-auto"
        data-testid="checkout-logo"
      />
    </div>

    {/* Customer Details */}
    <div className="p-6 rounded-2xl border border-border bg-background/50">
      <h2 className="text-xl font-bold text-foreground mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Customer Details</h2>
      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
          <input type="text" name="name" value={customerInfo.name} onChange={handleInputChange} className={inputClass('name')} placeholder="John Smith" data-testid="customer-name-input" />
          {formErrors.name && <p className="text-red-500 text-xs mt-1">{formErrors.name}</p>}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
            <input type="email" name="email" value={customerInfo.email} onChange={handleInputChange} className={inputClass('email')} placeholder="john@example.com" data-testid="customer-email-input" />
            {formErrors.email && <p className="text-red-500 text-xs mt-1">{formErrors.email}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
            <input type="tel" name="phone" value={customerInfo.phone} onChange={handleInputChange} className={inputClass('phone')} placeholder="0412 345 678" data-testid="customer-phone-input" />
            {formErrors.phone && <p className="text-red-500 text-xs mt-1">{formErrors.phone}</p>}
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Billing Address *</label>
          <input type="text" name="billingStreet" value={customerInfo.billingStreet} onChange={handleInputChange} className={inputClass('billingStreet')} placeholder="123 Honey Lane" data-testid="billing-street-input" />
          {formErrors.billingStreet && <p className="text-red-500 text-xs mt-1">{formErrors.billingStreet}</p>}
        </div>
        <div>
          <label className="block text-sm font-medium text-foreground mb-1">Address Line 2</label>
          <input type="text" name="billingStreet2" value={customerInfo.billingStreet2} onChange={handleInputChange} className={inputClass('billingStreet2')} placeholder="Unit, Apartment, Suite (optional)" data-testid="billing-street2-input" />
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Suburb *</label>
            <input type="text" name="billingSuburb" value={customerInfo.billingSuburb} onChange={handleInputChange} className={inputClass('billingSuburb')} placeholder="Melbourne" data-testid="billing-suburb-input" />
            {formErrors.billingSuburb && <p className="text-red-500 text-xs mt-1">{formErrors.billingSuburb}</p>}
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">State</label>
            <select name="billingState" value={customerInfo.billingState} onChange={handleInputChange} className={inputClass('billingState')} data-testid="billing-state-input">
              <option value="VIC">VIC</option>
              <option value="NSW">NSW</option>
              <option value="QLD">QLD</option>
              <option value="NT">NT</option>
              <option value="ACT">ACT</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-foreground mb-1">Postcode *</label>
            <input type="text" name="billingPostcode" value={customerInfo.billingPostcode} onChange={handleInputChange} className={inputClass('billingPostcode')} placeholder="3000" data-testid="billing-postcode-input" />
            {formErrors.billingPostcode && <p className="text-red-500 text-xs mt-1">{formErrors.billingPostcode}</p>}
          </div>
        </div>
      </div>
    </div>

    {/* Shipping Method */}
    <div className="p-6 rounded-2xl border border-border bg-background/50">
      <h2 className="text-xl font-bold text-foreground mb-6" style={{ fontFamily: 'Outfit, sans-serif' }}>Shipping Method</h2>
      <div className="space-y-3">
        <button onClick={() => setShippingMethod('postage')} className={`w-full flex items-center space-x-4 p-4 rounded-xl border-2 transition-all ${shippingMethod === 'postage' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid="shipping-postage-option">
          <Truck className={`w-6 h-6 ${shippingMethod === 'postage' ? 'text-primary' : 'text-muted-foreground'}`} />
          <div className="flex-1 text-left">
            <p className="font-semibold text-foreground">Standard Postage</p>
            <p className="text-sm text-muted-foreground">Australia-wide delivery</p>
          </div>
          <span className="font-semibold text-foreground">$11.95</span>
        </button>
        <button onClick={() => setShippingMethod('pickup')} className={`w-full flex items-center space-x-4 p-4 rounded-xl border-2 transition-all ${shippingMethod === 'pickup' ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50'}`} data-testid="shipping-pickup-option">
          <MapPin className={`w-6 h-6 ${shippingMethod === 'pickup' ? 'text-primary' : 'text-muted-foreground'}`} />
          <div className="flex-1 text-left">
            <p className="font-semibold text-foreground">Local Pickup</p>
            <p className="text-sm text-muted-foreground">Malvern East, Melbourne</p>
          </div>
          <span className="font-semibold text-green-600">FREE</span>
        </button>
      </div>
    </div>

    {/* Shipping Address */}
    {shippingMethod === 'postage' && (
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="p-6 rounded-2xl border border-border bg-background/50">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground" style={{ fontFamily: 'Outfit, sans-serif' }}>Shipping Address</h2>
          <label className="flex items-center space-x-2 cursor-pointer" data-testid="same-as-billing-checkbox">
            <input type="checkbox" checked={sameAsBilling} onChange={(e) => setSameAsBilling(e.target.checked)} className="w-4 h-4 rounded border-border text-primary focus:ring-primary" />
            <span className="text-sm text-foreground/70">Same as billing</span>
          </label>
        </div>
        {!sameAsBilling && (
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Full Name *</label>
              <input type="text" name="shippingName" value={customerInfo.shippingName} onChange={handleInputChange} className={inputClass('shippingName')} placeholder="John Smith" data-testid="shipping-name-input" />
              {formErrors.shippingName && <p className="text-red-500 text-xs mt-1">{formErrors.shippingName}</p>}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Email *</label>
                <input type="email" name="shippingEmail" value={customerInfo.shippingEmail} onChange={handleInputChange} className={inputClass('shippingEmail')} placeholder="john@example.com" data-testid="shipping-email-input" />
                {formErrors.shippingEmail && <p className="text-red-500 text-xs mt-1">{formErrors.shippingEmail}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Phone *</label>
                <input type="tel" name="shippingPhone" value={customerInfo.shippingPhone} onChange={handleInputChange} className={inputClass('shippingPhone')} placeholder="0412 345 678" data-testid="shipping-phone-input" />
                {formErrors.shippingPhone && <p className="text-red-500 text-xs mt-1">{formErrors.shippingPhone}</p>}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Street Address *</label>
              <input type="text" name="street" value={customerInfo.street} onChange={handleInputChange} className={inputClass('street')} placeholder="123 Honey Lane" data-testid="address-street-input" />
              {formErrors.street && <p className="text-red-500 text-xs mt-1">{formErrors.street}</p>}
            </div>
            <div>
              <label className="block text-sm font-medium text-foreground mb-1">Address Line 2</label>
              <input type="text" name="street2" value={customerInfo.street2} onChange={handleInputChange} className={inputClass('street2')} placeholder="Unit, Apartment, Suite (optional)" data-testid="address-street2-input" />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Suburb *</label>
                <input type="text" name="suburb" value={customerInfo.suburb} onChange={handleInputChange} className={inputClass('suburb')} placeholder="Melbourne" data-testid="address-suburb-input" />
                {formErrors.suburb && <p className="text-red-500 text-xs mt-1">{formErrors.suburb}</p>}
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">State</label>
                <select name="state" value={customerInfo.state} onChange={handleInputChange} className={inputClass('state')} data-testid="address-state-input">
                  <option value="VIC">VIC</option>
                  <option value="NSW">NSW</option>
                  <option value="QLD">QLD</option>
                  <option value="NT">NT</option>
                  <option value="ACT">ACT</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-foreground mb-1">Postcode *</label>
                <input type="text" name="postcode" value={customerInfo.postcode} onChange={handleInputChange} className={inputClass('postcode')} placeholder="3000" data-testid="address-postcode-input" />
                {formErrors.postcode && <p className="text-red-500 text-xs mt-1">{formErrors.postcode}</p>}
              </div>
            </div>
          </div>
        )}
        {sameAsBilling && (
          <p className="text-sm text-foreground/60">Shipping address will use the customer billing details provided above.</p>
        )}
      </motion.div>
    )}
  </div>
);

export default CheckoutForm;
